import { createTheme, responsiveFontSizes } from '@material-ui/core/styles'
import { LANGUAGES } from '../constants'
import { arabicFontFace, englishFontFace } from './fonts'

// AVA theme
const theme = (language = LANGUAGES.EN) =>
  responsiveFontSizes(
    createTheme({
      direction: language === LANGUAGES.AR ? 'rtl' : 'ltr',
      palette: {
        primary: {
          light: '#ccffff',
          main: '#00ffff',
          dark: '#04e0e0',
        },
        secondary: {
          main: '#000000',
        },
        warning: {
          main: '#ffea50',
        },
        error: {
          main: '#ff3366',
        },
        success: {
          main: '#81ff3f',
        },
        background: {
          default: '#fff',
          shadow: 'rgba(0, 0, 0, 0.16)',
          grey: '#f2f2f2',
          grey2: '#f5f5f5',
          calculator: 'rgba(247, 247, 247, 0.6)',
          black: '#000000',
          header: 'rgba(255, 255, 255, 0.5)',
          productFeatureLightGrey: 'rgba(247, 247, 247, 0.6)',
          productFeatureLightBlue: 'rgba(217, 255, 255, 0.6)',
        },
        text: {
          primary: '#000000',
          secondary: '#ffffff',
          footerInfo: '#CCCCCC',
          link: '#006060',
          lightGrey: '#6E7171',
          sliderTrackColor: '#009999',
          shadow: '2px 2px 0px #00FFFF',
        },
        stroke: {
          arabicXray: '#9c9c9c',
        },
        grey: {
          600: '#727272',
          500: '#999999',
          400: '#CCCCCC',
          300: '#f1f1f1',
        },
        button: {
          main: '#369999',
        },
        footer: {
          text: '#ffffff',
          background: '#000000',
        },
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 960,
          mxl: 1080,
          lg: 1440,
          xl: 1920,
        },
      },
      typography: {
        fontFamily:
          language === LANGUAGES.AR ? 'SSTArabic, Arial' : 'NeuzeitGro, Arial',
        h1: {
          fontSize: 54,
          fontWeight: 500,
          lineHeight: 1.2,
        },
        h2: {
          fontWeight: 500,
          lineHeight: 1.08,
        },
        h3: {
          '@media (min-width:600px)': {
            fontSize: 42,
          },
          fontWeight: 500,
        },
        h4: {
          '@media (min-width:600px)': {
            fontSize: 32,
          },
          fontWeight: 500,
        },
        body1: {
          fontSize: 16,
          '@media (min-width:600px)': {
            fontSize: 18,
          },
          lineHeight: 1.3,
        },
      },
      zIndex: {
        xRayWrapper: 1,
        xRayText: -2,
        slash: -1,
        blogImageCategory: 2,
        blogNavbar: 3,
        appBar: 1301,
        blogShareNavbar: 990,
        blogShareTooltip: 991,
        resetButton: 1,
      },
      overrides: {
        MuiContainer: {
          root: {
            '@media (max-width:599px)': {
              paddingLeft: '24px',
              paddingRight: '24px',
            },
          },
        },
        MuiTab: {
          root: {
            marginRight: '8px',
            borderRadius: '4px',
            padding: '4px 16px',
            border: '2px solid #727272',
            '@media (min-width:600px)': {
              minWidth: 'fit-content',
              minHeight: 'fit-content',
            },
          },
        },
        MuiInputAdornment: {
          root: {
            '& .MuiTypography-colorTextSecondary': {
              color: '#000',
              lineHeight: 1.5,
            },
          },
        },
        MuiCssBaseline: {
          '@global': {
            '@font-face': [...arabicFontFace, ...englishFontFace],
            '::-webkit-input-placeholder': {
              /* Chrome/Opera/Safari */ color: '#818181',
            },
            '::-moz-placeholder': {
              /* Firefox 19+ */ color: '#818181',
            },
            ':-ms-input-placeholder': {
              /* IE 10+ */ color: '#818181',
            },
            ':-moz-placeholder': {
              /* Firefox 18- */ color: '#818181',
            },
            body: {
              backgroundColor: '#fff',
            },
            '.grecaptcha-badge': {
              opacity: '0',
              visibility: 'hidden',
              '&>*': {
                opacity: '0',
                visibility: 'hidden',
              },
            },
          },
        },
        MuiAccordion: {
          root: {
            boxShadow: 'none',
            '&:not(:last-child)': {
              borderBottom: 0,
            },
            '&:before': {
              display: 'none',
            },
            '&$expanded': {
              margin: 'auto',
            },
          },
          expanded: {},
        },
        MuiAccordionSummary: {
          root: {
            fontWeight: '500',
            padding: '0 0 8px 0',
            minHeight: 74,
            '&$expanded': {
              minHeight: 74,
            },
          },
          content: {
            margin: '12px 0 0',
            '&$expanded': {
              margin: '12px 0 0',
            },
          },
          expandIcon: {
            position: 'relative',
            top: '0',
            '& .MuiSvgIcon-root': {
              fill: 'black',
              width: '1.4em',
              height: '1.4em',
            },
          },
        },
        MuiAccordionDetails: {
          root: {
            padding: '24px 72px 24px 0',
            '@media (max-width:599px)': {
              padding: '24px 0',
            },
            lineHeight: 1.67,
          },
        },
        MuiButton: {
          root: {
            padding: '0.625rem 1.25rem',
            textTransform: 'none',
          },
        },
        MuiInput: {
          underline: {
            '&:hover:not(.Mui-disabled):before': {
              borderBottom: () => '1px solid #000',
            },
            '&:before': {
              borderBottom: () => '1px solid #000',
            },
            '&:after': {
              borderBottom: () => '1px solid #000',
            },
          },
        },
        MuiInputBase: {
          input: {
            padding: '1rem 0',
          },
        },
        MuiLink: {
          root: {
            color: '#006060',
            fontWeight: 500,
          },
        },
      },
    }),
  )

// Personal Finance Theme
export const personalTheme = (language = LANGUAGES.EN) =>
  createTheme({
    ...theme(language),
    palette: {
      ...theme(language).palette,
      primary: {
        light: '#ccffff',
        main: '#006060',
        dark: '#04e0e0',
      },
      secondary: {
        main: '#fff',
      },
      warning: {
        main: '#ffea50',
      },
      error: {
        main: '#ff3366',
      },
      success: {
        main: '#81ff3f',
      },
      background: {
        default: '#000',
        shadow: 'rgba(0, 0, 0, 0.16)',
        grey: 'rgba(0,0,0, 0.5)',
        grey2: '#f5f5f5',
        calculator: 'rgba(32,32,32, 0.6)',
        black: '#fff',
        header: 'rgba(0, 0, 0, 0.5)',
        productFeatureLightGrey: 'rgba(32, 32, 32, 0.8)',
        productFeatureLightBlue: 'rgba(0, 37, 37, 0.8)',
      },
      text: {
        primary: '#fff',
        secondary: '#000',
        footerInfo: '#CCCCCC',
        link: '#00ffff',
        lightGrey: '#6E7171',
        sliderTrackColor: '#009999',
        shadow: '2px 2px 0px #009999',
      },
      stroke: {
        arabicXray: '#9c9c9c',
      },
      grey: {
        600: '#727272',
        500: '#999999',
        400: '#CCCCCC',
        300: '#f1f1f1',
      },
      button: {
        main: '#369999',
      },
    },
    overrides: {
      ...theme(language).overrides,
      MuiCssBaseline: {
        '@global': {
          '@font-face': [...arabicFontFace, ...englishFontFace],
          '::-webkit-input-placeholder': {
            /* Chrome/Opera/Safari */ color: '#818181',
          },
          '::-moz-placeholder': {
            /* Firefox 19+ */ color: '#818181',
          },
          ':-ms-input-placeholder': {
            /* IE 10+ */ color: '#818181',
          },
          ':-moz-placeholder': {
            /* Firefox 18- */ color: '#818181',
          },
          body: {
            backgroundColor: '#000',
          },
          '.grecaptcha-badge': {
            opacity: '0',
            visibility: 'hidden',
            '&>*': {
              opacity: '0',
              visibility: 'hidden',
            },
          },
        },
      },
      MuiInput: {
        underline: {
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: () => '1px solid #fff',
          },
          '&:before': {
            borderBottom: () => '1px solid #fff',
          },
          '&:after': {
            borderBottom: () => '1px solid #fff',
          },
        },
      },
      MuiLink: {
        root: {
          color: '#00ffff',
          fontWeight: 500,
        },
      },
    },
  })

export default theme
